import {
  AuthenticationDetails,
  CognitoRefreshToken,
  CognitoUser,
  CognitoUserPool,
  type CognitoUserSession,
} from 'amazon-cognito-identity-js';
import { appOrigin, config } from '../config.js';

export interface Tokens {
  idToken: string;
  accessToken: string;
  refreshToken: string;
  /** Epoch milliseconds. */
  expiresAt: number;
  /** Google users refresh through the hosted endpoint; native users through the pool. */
  federated: boolean;
  email: string;
}

export type SignInStep =
  | { kind: 'done'; tokens: Tokens }
  | { kind: 'newPassword'; user: CognitoUser }
  | { kind: 'totpRequired'; user: CognitoUser }
  | { kind: 'mfaSetup'; user: CognitoUser; secret: string };

export class AuthError extends Error {
  constructor(
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = 'AuthError';
  }
}

function pool(): CognitoUserPool {
  if (!config.cognito.userPoolId || !config.cognito.clientId) {
    throw new AuthError('not_configured', 'Sign-in is not configured for this build.');
  }
  return new CognitoUserPool({ UserPoolId: config.cognito.userPoolId, ClientId: config.cognito.clientId });
}

function fromSession(session: CognitoUserSession, federated: boolean): Tokens {
  const id = session.getIdToken();
  const payload = id.payload as { email?: string; exp?: number };
  return {
    idToken: id.getJwtToken(),
    accessToken: session.getAccessToken().getJwtToken(),
    refreshToken: session.getRefreshToken().getToken(),
    expiresAt: (payload.exp ?? Math.floor(Date.now() / 1000) + 3600) * 1000,
    federated,
    email: (payload.email ?? '').toLowerCase(),
  };
}

function wrapError(err: unknown): AuthError {
  const e = err as { code?: string; name?: string; message?: string };
  const code = e.code ?? e.name ?? 'auth_error';
  switch (code) {
    case 'NotAuthorizedException':
      if (/password attempts exceeded/i.test(e.message ?? '')) return new AuthError('locked', 'Too many failed attempts.');
      return new AuthError('invalid', 'That email and password do not match. Check both and try again.');
    case 'UserNotFoundException':
      return new AuthError('invalid', 'That email and password do not match. Check both and try again.');
    case 'CodeMismatchException':
      return new AuthError('code', 'That code is not right. Check your authenticator and try again.');
    case 'EnableSoftwareTokenMFAException':
      return new AuthError('code', 'That code is not right. Check your authenticator and try again.');
    case 'ExpiredCodeException':
      return new AuthError('expired', 'That code has expired. Request a new one.');
    case 'InvalidPasswordException':
      return new AuthError('password', 'Use at least 12 characters with upper and lower case, a number and a symbol.');
    case 'LimitExceededException':
      return new AuthError('limit', 'Too many attempts. Wait a few minutes and try again.');
    case 'UserLambdaValidationException':
      // Our triggers refuse with a designed message after "PreAuthentication failed with error ".
      return new AuthError('refused', (e.message ?? '').replace(/^.*failed with error /, '').replace(/\.$/, '') + '.');
    default:
      return new AuthError(code, e.message ?? 'Something went wrong. Try again.');
  }
}

/** Shared callbacks for the SRP flow and its follow-up challenges. */
function challengeCallbacks(user: CognitoUser, resolve: (s: SignInStep) => void, reject: (e: AuthError) => void) {
  return {
    onSuccess: (session: CognitoUserSession) => resolve({ kind: 'done', tokens: fromSession(session, false) }),
    onFailure: (err: unknown) => reject(wrapError(err)),
    newPasswordRequired: () => resolve({ kind: 'newPassword', user }),
    totpRequired: () => resolve({ kind: 'totpRequired', user }),
    mfaSetup: () => {
      user.associateSoftwareToken({
        associateSecretCode: (secret: string) => resolve({ kind: 'mfaSetup', user, secret }),
        onFailure: (err: unknown) => reject(wrapError(err)),
      });
    },
    mfaRequired: () => reject(new AuthError('unsupported', 'SMS codes are not used here. Ask your administrator to reset your second factor.')),
    selectMFAType: () => user.sendMFASelectionAnswer('SOFTWARE_TOKEN_MFA', challengeCallbacks(user, resolve, reject)),
  };
}

export function signIn(email: string, password: string): Promise<SignInStep> {
  const user = new CognitoUser({ Username: email.trim().toLowerCase(), Pool: pool() });
  const details = new AuthenticationDetails({ Username: email.trim().toLowerCase(), Password: password });
  return new Promise((resolve, reject) => user.authenticateUser(details, challengeCallbacks(user, resolve, reject)));
}

export function completeNewPassword(user: CognitoUser, newPassword: string): Promise<SignInStep> {
  return new Promise((resolve, reject) =>
    user.completeNewPasswordChallenge(newPassword, {}, challengeCallbacks(user, resolve, reject)),
  );
}

export function answerTotp(user: CognitoUser, code: string): Promise<Tokens> {
  return new Promise((resolve, reject) =>
    user.sendMFACode(
      code,
      {
        onSuccess: (session: CognitoUserSession) => resolve(fromSession(session, false)),
        onFailure: (err: unknown) => reject(wrapError(err)),
      },
      'SOFTWARE_TOKEN_MFA',
    ),
  );
}

export function confirmTotpSetup(user: CognitoUser, code: string): Promise<Tokens> {
  return new Promise((resolve, reject) =>
    user.verifySoftwareToken(code, 'Titan', {
      onSuccess: (session: CognitoUserSession) => resolve(fromSession(session, false)),
      onFailure: (err: unknown) => reject(wrapError(err)),
    }),
  );
}

export function otpauthUrl(secret: string, email: string): string {
  return `otpauth://totp/${encodeURIComponent('Titan by DinoCloud')}:${encodeURIComponent(email)}?secret=${secret}&issuer=${encodeURIComponent('Titan by DinoCloud')}`;
}

export function forgotPassword(email: string): Promise<void> {
  const user = new CognitoUser({ Username: email.trim().toLowerCase(), Pool: pool() });
  return new Promise((resolve, reject) =>
    user.forgotPassword({ onSuccess: () => resolve(), onFailure: (err: unknown) => reject(wrapError(err)), inputVerificationCode: () => resolve() }),
  );
}

export function confirmForgotPassword(email: string, code: string, newPassword: string): Promise<void> {
  const user = new CognitoUser({ Username: email.trim().toLowerCase(), Pool: pool() });
  return new Promise((resolve, reject) =>
    user.confirmPassword(code, newPassword, { onSuccess: () => resolve(), onFailure: (err: unknown) => reject(wrapError(err)) }),
  );
}

// Google, through the hosted UI with PKCE.

const PKCE_KEY = 'titan.pkce';

function base64url(bytes: ArrayBuffer): string {
  return btoa(String.fromCharCode(...new Uint8Array(bytes))).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export function googleSignInUrl(): Promise<string> {
  return federatedSignInUrl('Google');
}

/** Hosted UI authorize URL for a federated provider: Google for staff, a tenant's SSO for clients. */
export async function federatedSignInUrl(provider: string): Promise<string> {
  if (!config.cognito.domain) throw new AuthError('not_configured', 'Single sign-on is not configured for this build.');
  const verifierBytes = crypto.getRandomValues(new Uint8Array(32));
  const verifier = base64url(verifierBytes.buffer);
  const challenge = base64url(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier)));
  const state = base64url(crypto.getRandomValues(new Uint8Array(16)).buffer);
  sessionStorage.setItem(PKCE_KEY, JSON.stringify({ verifier, state }));
  const params = new URLSearchParams({
    identity_provider: provider,
    redirect_uri: `${appOrigin()}/auth/callback`,
    response_type: 'code',
    client_id: config.cognito.clientId,
    scope: 'openid email profile',
    code_challenge_method: 'S256',
    code_challenge: challenge,
    state,
  });
  return `https://${config.cognito.domain}/oauth2/authorize?${params.toString()}`;
}

export async function exchangeCode(code: string, state: string): Promise<Tokens> {
  const raw = sessionStorage.getItem(PKCE_KEY);
  sessionStorage.removeItem(PKCE_KEY);
  const saved = raw ? (JSON.parse(raw) as { verifier: string; state: string }) : null;
  if (!saved || saved.state !== state) throw new AuthError('state', 'The sign-in could not be verified. Start again.');
  const res = await fetch(`https://${config.cognito.domain}/oauth2/token`, {
    method: 'POST',
    headers: { 'content-type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({
      grant_type: 'authorization_code',
      client_id: config.cognito.clientId,
      code,
      redirect_uri: `${appOrigin()}/auth/callback`,
      code_verifier: saved.verifier,
    }),
  });
  if (!res.ok) throw new AuthError('exchange', 'Google sign-in did not complete. Try again.');
  return tokensFromOAuth((await res.json()) as OAuthTokens, undefined);
}

interface OAuthTokens {
  id_token: string;
  access_token: string;
  refresh_token?: string;
  expires_in: number;
}

function tokensFromOAuth(t: OAuthTokens, previousRefresh: string | undefined): Tokens {
  const payload = JSON.parse(atob(t.id_token.split('.')[1]!.replace(/-/g, '+').replace(/_/g, '/'))) as { email?: string; exp?: number };
  return {
    idToken: t.id_token,
    accessToken: t.access_token,
    refreshToken: t.refresh_token ?? previousRefresh ?? '',
    expiresAt: (payload.exp ?? Math.floor(Date.now() / 1000) + t.expires_in) * 1000,
    federated: true,
    email: (payload.email ?? '').toLowerCase(),
  };
}

export async function refreshTokens(current: Tokens): Promise<Tokens> {
  if (current.federated) {
    const res = await fetch(`https://${config.cognito.domain}/oauth2/token`, {
      method: 'POST',
      headers: { 'content-type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams({ grant_type: 'refresh_token', client_id: config.cognito.clientId, refresh_token: current.refreshToken }),
    });
    if (!res.ok) throw new AuthError('refresh', 'Your session has ended. Sign in again.');
    return tokensFromOAuth((await res.json()) as OAuthTokens, current.refreshToken);
  }
  const user = new CognitoUser({ Username: current.email, Pool: pool() });
  return new Promise((resolve, reject) =>
    user.refreshSession(new CognitoRefreshToken({ RefreshToken: current.refreshToken }), (err: unknown, session: CognitoUserSession) => {
      if (err) reject(new AuthError('refresh', 'Your session has ended. Sign in again.'));
      else resolve({ ...fromSession(session, false), refreshToken: current.refreshToken });
    }),
  );
}

export function signOutUrl(): string | null {
  if (!config.cognito.domain) return null;
  const params = new URLSearchParams({ client_id: config.cognito.clientId, logout_uri: `${appOrigin()}/auth/sign-in` });
  return `https://${config.cognito.domain}/logout?${params.toString()}`;
}
